import React, { useState } from 'react';
import { Car, Shield  } from 'lucide-react';

// Step components
import QuoteResponseStep from './QuoteResponse';
import QuoteWidgetForm from './QuoteWidgetForm';
import { useSelector } from 'react-redux';
import { getPendingActions } from '../../../../../../states/slices/globalSlice';

interface IQuoteWidgetCalculator {
    onclose: ()=> void;
}
const QuoteWidgetCalculator: React.FC<IQuoteWidgetCalculator> = ({onclose}) => {

    // Wizard state
    const [currentStep, setCurrentStep] = useState(1);

    // Form data
    const [wizardData, setWizardData] = useState<PolicyWizardData>({
        formData: {
            registrationYear: undefined,
            insuredValue: undefined,
            maxSeat: undefined,
            insurerPortfolioId: undefined,
            excessBought: undefined,
            riskTypeId: undefined,
            extraTppdlLimit: undefined,
            umbrellaLimit: undefined,
            startDate: undefined,
            endDate: undefined,
            insuranceCompany: undefined,
            currency: 'GHS',
            currencyRate: undefined,
            leadSource: undefined,
            applyNcd: null
        },
        quoteResponse: null
    });

    // Step configuration
    const steps = [
        {
            number: 1,
            title: 'Vehicle and Policy Details',
            icon: Car,
            description: 'Vehicle and policy information and specifications'
        },
        {
            number: 2,
            title: 'Quote Response',
            icon: Shield,
            description: 'Quote response'
        },
    ];

    // Update wizard data
    const updateWizardData = (stepData: any, stepKey: keyof PolicyWizardData) => {
        setWizardData(prev => {

            if ('quoteResponse' in stepData) {
                return {
                    ...prev,
                    formData: {
                        ...prev.formData,
                        ...stepData
                    },
                    quoteResponse: stepData.quoteResponse
                };
            }

            return {
                ...prev,
                [stepKey]: stepData
            };
        });
    };

    // Navigation handlers
    const goToNextStep = () => {
        if (currentStep < steps.length) {
            setCurrentStep(currentStep + 1);
        }
    };

    const goToPreviousStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Render current step
    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <QuoteWidgetForm
                        formData={wizardData.formData}
                        onUpdate={(data: any) => updateWizardData(data, 'formData')}
                        onNext={goToNextStep}
                        onclose={onclose}
                    />
                );
            case 2:
                return (
                    <QuoteResponseStep
                        data={wizardData.quoteResponse}
                        formData={wizardData.formData}
                        onPrevious={goToPreviousStep}
                        onclose={onclose}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="mx-auto">
            {renderCurrentStep()}
        </div>
    );
};

export default QuoteWidgetCalculator;


// Interfaces
export interface FormData {
    registrationYear?: number,
    insuredValue?: number,
    maxSeat?: number,
    insurerPortfolioId?: number,
    excessBought?: boolean,
    riskTypeId?: number,
    extraTppdlLimit?: number,
    umbrellaLimit?: number,
    startDate?: Date,
    endDate?: Date,
    insuranceCompany?: string,
    currency?: string,
    currencyRate?: number,
    leadSource?: string
    applyNcd: boolean | null
    ncdRate?: number
    customFleetDiscountPercentage?: number;
}

export interface QuoteResponse {
    additionalPremium: number,
    annualPremium: number,
    basePremium: number,
    calculatedAt: string,
    currencyCode: { key: string, displayName: string, description: string },
    excessPremium: number,
    installments: {
        [key: string]: {
            totalPremium: number;
            initialDeposit: number;
            period: number;
            monthlyInstallment: number;
            outRightPremium: number;
            totalInterest: number;
            excess: number;
        };
    },
    excess: number,
    initialDeposit: number,
    monthlyInstallment: number,
    outRightPremium: number,
    period: number,
    totalInterest: number,
    totalPremium: number,
    insurerPortfolio: { id: number, name: string, code: string },
    noClaimDiscount: number,
    noClaimDiscountPercentage: number,
    policyDurationDays: number,
    premiumAmount: number,
    proRatePercentage: number,
    quoteReference: string,
    riskType: { id: number, name: string, code: string, basePremium: number, ownDamageRate: number },
    validityDays: number,
    vehicleAge: number,
}

export interface PolicyWizardData {
    formData: FormData;
    quoteResponse: QuoteResponse | null;

}
